import React from "react";
import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import { GlobalWorkerOptions, getDocument } from "pdfjs-dist";
import {
  getProofDisplayFileName,
  isImageUrl,
  isPdfUrl,
  isSvgUrl,
  isVectorDocUrl,
} from "./proofRowUtils";
import { resolveGeneratedVectorPreview } from "./smartProofPreviewUtils";

GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString();

const PREVIEW_LOCK_W = 760;
const PREVIEW_H = { xs: 260, sm: 340, md: 420 };
const COMPACT_PDF_RENDER_W = 320;
const COMPACT_PDF_RENDER_H = 220;
const COMPACT_PDF_MAX_SCALE = 1;
const PDF_RENDER_TIMEOUT_MS = 8000;
const pdfThumbCache = new Map();
const pdfThumbPromiseCache = new Map();
const pdfThumbWarningKeys = new Set();
const vectorPreviewWarningKeys = new Set();

const PreviewEyeAction = ({ title, ariaLabel, disabled, onClick, top = 8, right = 8 }) => (
  <Tooltip title={title} arrow placement="left">
    <Box
      component="span"
      sx={{
        position: "absolute",
        top,
        right,
        zIndex: 3,
        display: "inline-flex",
      }}
    >
      <IconButton
        size="small"
        aria-label={ariaLabel}
        onClick={onClick}
        disabled={disabled}
        sx={{
          bgcolor: "white",
          boxShadow: 1,
          "&:hover": { bgcolor: "white" },
        }}
      >
        <VisibilityOutlinedIcon fontSize="small" />
      </IconButton>
    </Box>
  </Tooltip>
);

const PreviewFileName = ({ fileName }) => (
  <Box
    className="smart-proof-preview__file-name"
    sx={{
      position: "absolute",
      left: 8,
      bottom: 8,
      zIndex: 3,
      display: "flex",
      alignItems: "center",
      gap: 0.5,
      maxWidth: "calc(100% - 16px)",
      overflow: "hidden",
      bgcolor: "rgba(255,255,255,0.94)",
      borderRadius: 1,
      px: 0.75,
      py: 0.25,
      boxShadow: 1,
      opacity: 0,
      pointerEvents: "none",
      transition: "opacity 150ms ease",
      ".smart-proof-preview:hover &": {
        opacity: 1,
      },
    }}
  >
    <Typography variant="caption" noWrap title={fileName}>
      {fileName}
    </Typography>
  </Box>
);

const buildPdfThumbCacheKey = ({ src, renderWidth, renderHeight, fitContainer }) =>
  `${src}|${Number(renderWidth) || 0}x${Number(renderHeight) || 0}|${fitContainer ? "compact" : "full"}`;

const retainPdfThumb = (cacheKey) => {
  const entry = pdfThumbCache.get(cacheKey);
  if (!entry?.url) return "";
  entry.refCount = (entry.refCount || 0) + 1;
  return entry.url;
};

const releasePdfThumb = (cacheKey) => {
  const entry = pdfThumbCache.get(cacheKey);
  if (!entry) return;
  entry.refCount = Math.max(0, (entry.refCount || 0) - 1);
  if (entry.refCount === 0) {
    URL.revokeObjectURL(entry.url);
    pdfThumbCache.delete(cacheKey);
  }
};

const cleanupUnretainedPdfThumb = (cacheKey) => {
  const entry = pdfThumbCache.get(cacheKey);
  if (entry && (entry.refCount || 0) === 0) {
    URL.revokeObjectURL(entry.url);
    pdfThumbCache.delete(cacheKey);
  }
};

const PdfThumb = React.memo(function PdfThumb({
  src,
  width = 720,
  height = 420,
  fitContainer = false,
  renderWidth = width,
  renderHeight = height,
  lazy = false,
  fallback = null,
  fileName = "",
}) {
  const cacheKey = React.useMemo(
    () => buildPdfThumbCacheKey({ src, renderWidth, renderHeight, fitContainer }),
    [fitContainer, renderHeight, renderWidth, src]
  );
  const [imageUrl, setImageUrl] = React.useState(() => {
    const entry = pdfThumbCache.get(cacheKey);
    return entry?.url || "";
  });
  const [imageLoaded, setImageLoaded] = React.useState(false);
  const [fail, setFail] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(() => !lazy);
  const rootRef = React.useRef(null);

  React.useEffect(() => {
    if (!lazy || isVisible) return undefined;
    const node = rootRef.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "160px 0px" }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [isVisible, lazy, src]);

  React.useEffect(() => {
    let cancelled = false;
    let retainedKey = "";
    setFail(false);
    setImageLoaded(false);
    if (!src || !isVisible) return undefined;

    const cachedUrl = retainPdfThumb(cacheKey);
    if (cachedUrl) {
      setImageUrl(cachedUrl);
      return () => {
        releasePdfThumb(cacheKey);
      };
    }
    setImageUrl("");

    const renderPromise =
      pdfThumbPromiseCache.get(cacheKey) ||
      (async () => {
        const task = getDocument({ url: src, withCredentials: false });
        let pdf = null;
        let page = null;
        let canvas = null;
        try {
          pdf = await Promise.race([
            task.promise,
            new Promise((_, reject) => {
              window.setTimeout(() => reject(new Error("PDF preview timed out")), PDF_RENDER_TIMEOUT_MS);
            }),
          ]);
          page = await pdf.getPage(1);
          const initialViewport = page.getViewport({ scale: 1 });
          const compactMode = fitContainer;
          const maxRenderWidth = compactMode
            ? Math.min(Number(renderWidth) || COMPACT_PDF_RENDER_W, COMPACT_PDF_RENDER_W)
            : Number(renderWidth) || PREVIEW_LOCK_W - 32;
          const maxRenderHeight = compactMode
            ? Math.min(Number(renderHeight) || COMPACT_PDF_RENDER_H, COMPACT_PDF_RENDER_H)
            : Number(renderHeight) || 420;
          const baseScale = Math.min(
            maxRenderWidth / initialViewport.width,
            maxRenderHeight / initialViewport.height
          );
          const minScale = compactMode ? 0.02 : 0.2;
          const finalScale = compactMode
            ? Math.min(Math.max(baseScale, minScale), COMPACT_PDF_MAX_SCALE)
            : Math.max(baseScale, minScale);
          const viewport = page.getViewport({ scale: finalScale });
          canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          if (!ctx) throw new Error("Missing canvas context");
          const canvasWidth = Math.floor(viewport.width);
          const canvasHeight = Math.floor(viewport.height);
          if (canvasWidth <= 1 || canvasHeight <= 1) {
            throw new Error("PDF preview canvas dimensions are too small");
          }
          canvas.width = canvasWidth;
          canvas.height = canvasHeight;
          await page.render({ canvasContext: ctx, viewport }).promise;
          const blob = await new Promise((resolve, reject) => {
            canvas.toBlob((nextBlob) => {
              if (!nextBlob) {
                reject(new Error("Failed to encode PDF preview image"));
                return;
              }
              if (nextBlob.size <= 0) {
                reject(new Error("Encoded PDF preview image is empty"));
                return;
              }
              resolve(nextBlob);
            }, "image/png");
          });
          const url = URL.createObjectURL(blob);
          pdfThumbCache.set(cacheKey, { url, refCount: 0 });
          return url;
        } finally {
          page?.cleanup?.();
          pdf?.cleanup?.();
          pdf?.destroy?.();
          if (canvas) {
            canvas.width = 0;
            canvas.height = 0;
          }
          task.destroy?.();
          pdfThumbPromiseCache.delete(cacheKey);
        }
      })();
    pdfThumbPromiseCache.set(cacheKey, renderPromise);

    (async () => {
      try {
        const url = await renderPromise;
        if (cancelled) {
          cleanupUnretainedPdfThumb(cacheKey);
          return;
        }
        retainedKey = cacheKey;
        retainPdfThumb(cacheKey);
        setImageUrl(url);
      } catch (error) {
        if (!cancelled) {
          if (!pdfThumbWarningKeys.has(cacheKey)) {
            pdfThumbWarningKeys.add(cacheKey);
            console.warn("[SmartProofPreview] PDF thumbnail unavailable; falling back to PDF card", {
              fileName,
              message: error?.message || String(error),
            });
          }
          setFail(true);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (retainedKey) {
        releasePdfThumb(retainedKey);
      } else {
        cleanupUnretainedPdfThumb(cacheKey);
      }
    };
  }, [cacheKey, fitContainer, isVisible, renderHeight, renderWidth, src]);

  const markImageFailed = () => {
    if (!pdfThumbWarningKeys.has(cacheKey)) {
      pdfThumbWarningKeys.add(cacheKey);
      console.warn("[SmartProofPreview] PDF thumbnail unavailable; falling back to PDF card", {
        fileName,
        message: "Generated PDF thumbnail image failed to load",
      });
    }
    releasePdfThumb(cacheKey);
    setImageUrl("");
    setImageLoaded(false);
    setFail(true);
  };

  if (fail) return fallback;
  if (!imageUrl) {
    return (
      <div
        ref={rootRef}
        style={{
          width: fitContainer ? "100%" : width,
          maxWidth: fitContainer ? "100%" : undefined,
          minWidth: fitContainer ? 0 : undefined,
          height: fitContainer ? "100%" : height,
          display: "grid",
          placeItems: "center",
          fontSize: 12,
          color: "#888",
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        {isVisible ? "loading..." : "PDF preview"}
      </div>
    );
  }

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      {!imageLoaded ? (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
          }}
        >
          {fallback}
        </div>
      ) : null}
      <img
        src={imageUrl}
        alt="PDF"
        onLoad={() => setImageLoaded(true)}
        onError={markImageFailed}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          display: imageLoaded ? "block" : "none",
        }}
      />
    </div>
  );
});

function SmartProofPreviewComponent({
  filePath,
  fileName,
  title,
  height = PREVIEW_H,
  disabled = false,
  fitContainer = false,
  onPreviewOpen,
  sx,
  pdfRenderWidth,
  pdfRenderHeight,
  lazyPdfPreview = false,
  previewImageUrl = "",
  previewUrl = "",
  previewStatus = null,
  previewKind = null,
}) {
  const displayFileName = getProofDisplayFileName({ fileName, filePath }) || "Proof";
  const Frame = ({ children, className }) => (
    <Box
      className={className}
      sx={{
        width: "100%",
        maxWidth: fitContainer ? "100%" : PREVIEW_LOCK_W,
        minWidth: fitContainer ? 0 : undefined,
        height,
        borderRadius: 2,
        overflow: "hidden",
        border: "1px dashed #bdbdbd",
        bgcolor: "#fafafa",
        display: "grid",
        placeItems: "center",
        boxSizing: "border-box",
        p: 1,
        ...sx,
      }}
    >
      {children}
    </Box>
  );

  const pdfLabel = isPdfUrl(filePath) ? displayFileName : "";
  const pdfFallbackCard = React.useMemo(() => (
    <Box
      sx={{
        display: "grid",
        gap: 1.25,
        placeItems: "center",
        alignContent: "center",
        textAlign: "center",
        width: "100%",
        height: "100%",
        px: 2,
      }}
    >
      <PictureAsPdfOutlinedIcon color="error" sx={{ fontSize: 42 }} />
      {onPreviewOpen ? (
        <Button
          variant="contained"
          size="small"
          onClick={() => onPreviewOpen(filePath, displayFileName)}
          disabled={disabled}
          sx={{ textTransform: "none", width: "100%", maxWidth: 280 }}
        >
          Open PDF
        </Button>
      ) : (
        <Button
          variant="contained"
          size="small"
          component="a"
          href={filePath}
          download={displayFileName}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ textTransform: "none", width: "100%", maxWidth: 280 }}
        >
          Open PDF
        </Button>
      )}
    </Box>
  ), [displayFileName, filePath, disabled, onPreviewOpen]);

  if (!filePath) {
    return (
      <Frame>
        <Typography>{title || "No proof file attached yet."}</Typography>
      </Frame>
    );
  }

  const generatedVectorPreview = resolveGeneratedVectorPreview({
    filePath,
    previewImageUrl,
    previewUrl,
    previewStatus,
    previewKind,
  });

  if (isVectorDocUrl(filePath) && generatedVectorPreview) {
    const openGeneratedPreview = () => {
      if (onPreviewOpen) onPreviewOpen(generatedVectorPreview, displayFileName);
      else window.open(generatedVectorPreview, "_blank", "noopener,noreferrer");
    };
    return (
      <Box sx={{ position: "relative", width: "100%", maxWidth: fitContainer ? "100%" : PREVIEW_LOCK_W }}>
        <SmartProofPreviewComponent
          filePath={generatedVectorPreview}
          fileName={displayFileName}
          title={title}
          height={height}
          disabled={disabled}
          fitContainer={fitContainer}
          onPreviewOpen={onPreviewOpen}
          sx={sx}
          pdfRenderWidth={pdfRenderWidth}
          pdfRenderHeight={pdfRenderHeight}
          lazyPdfPreview={lazyPdfPreview}
        />
        <PreviewEyeAction
          title="Open preview"
          ariaLabel="Preview generated artwork"
          onClick={openGeneratedPreview}
          disabled={disabled}
        />
      </Box>
    );
  }

  if (isPdfUrl(filePath)) {
    return (
      <Box
        sx={{
          width: "100%",
          alignContent: "center",
          maxWidth: fitContainer ? "100%" : undefined,
          minWidth: fitContainer ? 0 : undefined,
          display: "grid",
          placeItems: "center",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            maxWidth: fitContainer ? "100%" : undefined,
            minWidth: fitContainer ? 0 : undefined,
            height: fitContainer ? "100%" : undefined,
            overflow: "hidden",
          }}
        >
          <Frame>
            <PdfThumb
              src={filePath}
              width={fitContainer ? "100%" : PREVIEW_LOCK_W - 32}
              height={fitContainer ? "100%" : 420}
              renderWidth={pdfRenderWidth || (fitContainer ? COMPACT_PDF_RENDER_W : PREVIEW_LOCK_W - 32)}
              renderHeight={pdfRenderHeight || (fitContainer ? COMPACT_PDF_RENDER_H : 420)}
              fitContainer={fitContainer}
              lazy={lazyPdfPreview}
              fallback={pdfFallbackCard}
              fileName={pdfLabel}
            />
          </Frame>
          <PreviewEyeAction
            title="Open PDF"
            ariaLabel="Preview PDF"
            onClick={() => onPreviewOpen?.(filePath, displayFileName)}
            disabled={disabled}
          />
          <Box sx={{ position: "absolute", left: 8, bottom: 8, zIndex: 3, display: "flex", alignItems: "center", gap: 0.5, maxWidth: "calc(100% - 16px)", overflow: "hidden", bgcolor: "rgba(255,255,255,0.94)", borderRadius: 1, px: 0.75, py: 0.25, boxShadow: 1 }}>
            <PictureAsPdfOutlinedIcon fontSize="small" />
            <Typography
              variant="caption"
              noWrap
              title={pdfLabel}
              sx={{
                minWidth: 0,
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "block",
              }}
            >
              {pdfLabel}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  if (isImageUrl(filePath) || isSvgUrl(filePath)) {
    const openImage = () => {
      window.open(filePath, "_blank", "noopener,noreferrer");
    };
    return (
      <Box sx={{ position: "relative", width: "100%", maxWidth: fitContainer ? "100%" : PREVIEW_LOCK_W }}>
        <Frame className="smart-proof-preview smart-proof-preview--image">
          <img
            className="smart-proof-preview__image"
            src={filePath}
            alt={displayFileName}
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              width: "auto",
              height: "auto",
              objectFit: "contain",
              display: "block",
            }}
          />
        </Frame>
        <PreviewEyeAction
          title="Open full-size image"
          ariaLabel="Open full-size image"
          onClick={openImage}
          disabled={disabled}
          top={15}
          right={15}
        />
        <PreviewFileName fileName={displayFileName} />
      </Box>
    );
  }

  if (isVectorDocUrl(filePath)) {
    const warningKey = `${filePath}|${String(previewStatus || "unavailable").toLowerCase()}`;
    if (!vectorPreviewWarningKeys.has(warningKey)) {
      vectorPreviewWarningKeys.add(warningKey);
      console.warn("[SmartProofPreview] Generated vector preview unavailable; showing download fallback", {
        fileName: displayFileName,
        previewStatus: previewStatus || null,
      });
    }
    const label = displayFileName.toUpperCase();
    return (
      <Frame>
        <Box sx={{ display: "grid", gap: 1, placeItems: "center" }}>
          <InsertDriveFileOutlinedIcon />
          <Typography variant="body2" fontWeight={700}>{label}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
            Preview not supported for .ai/.eps. Download to view in Illustrator or compatible software.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownloadOutlinedIcon />}
            component="a"
            href={filePath}
            download={displayFileName}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ textTransform: "none" }}
          >
            Download
          </Button>
        </Box>
      </Frame>
    );
  }

  const fallbackName = displayFileName;
  return (
    <Frame>
      <Box sx={{ display: "grid", gap: 1, placeItems: "center" }}>
        <InsertDriveFileOutlinedIcon />
        <Typography variant="body2" fontWeight={700} noWrap title={fallbackName}>
          {fallbackName}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
          This file type does not have an in-browser preview. You can still download it.
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<FileDownloadOutlinedIcon />}
          component="a"
          href={filePath}
          download={fallbackName || undefined}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ textTransform: "none" }}
        >
          Download
        </Button>
      </Box>
    </Frame>
  );
}

export default React.memo(SmartProofPreviewComponent);
